/**
 * MonthDetailView - Detail view for drilling into specific month data
 *
 * Features:
 * - Day-by-day breakdown
 * - Violation/crash/inspection breakdown for that month
 * - State distribution
 * - Comparison to other months
 * - BASIC category pie chart
 * - Detailed data table
 */

import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import {
  Calendar,
  TrendingUp,
  TrendingDown,
  MapPin,
  AlertTriangle,
  CheckCircle,
  Activity,
  Clock,
} from 'lucide-react';
import { useDrillDown } from '../../contexts/DrillDownContext';
import type { Violation, Crash, Inspection } from '../../types/api';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface MonthData {
  month: string;
  violations: number;
  inspections: number;
  crashes: number;
  oosRate?: number;
}

interface ComparisonData {
  industryAverage?: number;
  peerAverage?: number;
  percentile?: number;
  trend?: 'up' | 'down' | 'stable';
}

interface CategoryChartData {
  category: string;
  count: number;
  percentage?: number;
}

interface StateChartData {
  state: string;
  count: number;
  violations?: number;
  inspections?: number;
}

interface MonthSummary {
  month: string;
  year: number;
  violationCount: number;
  crashCount: number;
  inspectionCount: number;
  oosRate: number;
  avgBasicScore: number;
}

interface SignificantChange {
  metric: string;
  change: number;
  isPositive: boolean;
  description: string;
}

interface MonthComparison {
  violationsDelta: number;
  crashesDelta: number;
  inspectionsDelta: number;
  violationsPercent: number;
  crashesPercent: number;
  inspectionsPercent: number;
  oosRateDelta: number;
  trend: 'improving' | 'worsening' | 'stable';
  significantChanges: SignificantChange[];
}

interface ViolationRecord {
  inspDate?: string;
  inspectionDate?: string;
  outOfServiceIndicator?: string;
  basicCategory?: string;
  reportState?: string;
  state?: string;
  [key: string]: unknown;
}

interface CrashRecord {
  reportDate?: string;
  crashDate?: string;
  fatalities?: number;
  injuries?: number;
  [key: string]: unknown;
}

interface InspectionRecord {
  inspDate?: string;
  inspectionDate?: string;
  reportState?: string;
  state?: string;
  [key: string]: unknown;
}

interface MonthDetailViewProps {
  data: {
    month?: string;
    violations?: Violation[];
    crashes?: Crash[];
    inspections?: Inspection[];
    category?: string;
    previousMonth?: MonthSummary | null;
    comparison?: MonthComparison;
  };
}

// ============================================================================
// CONSTANTS
// ============================================================================

import { BASIC_COLORS, CHART_COLORS } from '../../constants/colors';

const DAY_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ============================================================================
// COMPONENT
// ============================================================================

const MonthDetailView: React.FC<MonthDetailViewProps> = ({ data }) => {
  const { drillDown } = useDrillDown();

  const monthLabel = data?.month || 'Selected Month';
  const violations = data?.violations || [];
  const crashes = data?.crashes || [];
  const inspections = data?.inspections || [];

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalViolations = violations.length;
    const totalCrashes = crashes.length;
    const totalInspections = inspections.length;
    const oosViolations = violations.filter(v => v.outOfServiceIndicator === 'Y').length;
    const fatalities = crashes.reduce((sum, c) => sum + (c.fatalities || 0), 0);
    const injuries = crashes.reduce((sum, c) => sum + (c.injuries || 0), 0);

    // Calculate comparison if previous month data available
    const prevViolations = data?.previousMonth?.violationCount || 0;
    const violationChange = prevViolations > 0
      ? ((totalViolations - prevViolations) / prevViolations * 100).toFixed(1)
      : null;

    return {
      totalViolations,
      totalCrashes,
      totalInspections,
      oosViolations,
      oosRate: totalViolations > 0 ? ((oosViolations / totalViolations) * 100).toFixed(1) : '0',
      fatalities,
      injuries,
      violationChange,
      inspectionRate: totalInspections > 0
        ? ((totalViolations / totalInspections) * 100).toFixed(1)
        : '0',
    };
  }, [violations, crashes, inspections, data]);

  // Day-by-day breakdown
  const dayByDayData = useMemo(() => {
    const dayMap = new Map<number, { violations: number; inspections: number; crashes: number }>();

    // Initialize all days of month
    for (let i = 1; i <= 31; i++) {
      dayMap.set(i, { violations: 0, inspections: 0, crashes: 0 });
    }

    violations.forEach(v => {
      const date = new Date(v.inspDate || v.inspectionDate);
      const day = date.getDate();
      if (dayMap.has(day)) {
        dayMap.get(day)!.violations++;
      }
    });

    inspections.forEach(i => {
      const date = new Date(i.inspDate || i.inspectionDate);
      const day = date.getDate();
      if (dayMap.has(day)) {
        dayMap.get(day)!.inspections++;
      }
    });

    crashes.forEach(c => {
      const date = new Date(c.reportDate || (c as Crash & { crashDate?: string }).crashDate || '');
      const day = date.getDate();
      if (dayMap.has(day)) {
        dayMap.get(day)!.crashes++;
      }
    });

    return Array.from(dayMap.entries())
      .map(([day, stats]) => ({ day, ...stats }))
      .filter(d => d.violations > 0 || d.inspections > 0 || d.crashes > 0);
  }, [violations, inspections, crashes]);

  // Day of week distribution
  const dayOfWeekDistribution = useMemo(() => {
    const dowMap = new Map<number, number>();
    for (let i = 0; i < 7; i++) {
      dowMap.set(i, 0);
    }

    violations.forEach(v => {
      const date = new Date(v.inspDate || v.inspectionDate);
      const dow = date.getDay();
      dowMap.set(dow, (dowMap.get(dow) || 0) + 1);
    });

    return Array.from(dowMap.entries())
      .map(([dow, count]) => ({ day: DAY_OF_WEEK[dow], count }));
  }, [violations]);

  // BASIC category distribution
  const basicDistribution = useMemo(() => {
    const categoryMap = new Map<string, number>();

    violations.forEach(v => {
      const category = v.basicCategory || 'Unknown';
      categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
    });

    return Array.from(categoryMap.entries())
      .map(([category, count]) => ({
        category,
        count,
        color: BASIC_COLORS[category] || CHART_COLORS[0],
      }))
      .sort((a, b) => b.count - a.count);
  }, [violations]);

  // Geographic distribution
  const geographicDistribution = useMemo(() => {
    const stateMap = new Map<string, { violations: number; inspections: number }>();

    violations.forEach(v => {
      const state = v.reportState || v.state || 'Unknown';
      if (!stateMap.has(state)) {
        stateMap.set(state, { violations: 0, inspections: 0 });
      }
      stateMap.get(state)!.violations++;
    });

    inspections.forEach(i => {
      const state = i.reportState || i.state || 'Unknown';
      if (!stateMap.has(state)) {
        stateMap.set(state, { violations: 0, inspections: 0 });
      }
      stateMap.get(state)!.inspections++;
    });

    return Array.from(stateMap.entries())
      .map(([state, stats]) => ({ state, ...stats }))
      .sort((a, b) => b.violations - a.violations)
      .slice(0, 10);
  }, [violations, inspections]);

  // Handle category click
  const handleCategoryClick = (category: string) => {
    const categoryViolations = violations.filter(v => v.basicCategory === category);
    drillDown('violation-category', category, {
      category,
      violations: categoryViolations,
      month: monthLabel,
    });
  };

  // Handle state click
  const handleStateClick = (state: string) => {
    const stateViolations = violations.filter(v => (v.reportState || v.state) === state);
    const stateInspections = inspections.filter(i => (i.reportState || i.state) === state);
    drillDown('state-detail', state, {
      state,
      violations: stateViolations,
      inspections: stateInspections,
      month: monthLabel,
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Month Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '1rem',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderLeft: '4px solid #3b82f6',
        borderRadius: '8px',
      }}>
        <Calendar size={28} style={{ color: '#3b82f6' }} />
        <div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0, color: 'var(--color-text)' }}>
            {monthLabel}
          </h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', margin: 0 }}>
            {data?.category ? `Filtered by: ${data.category}` : 'All violations, crashes, and inspections'}
          </p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="drill-down-stats-row">
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value">{summaryStats.totalViolations.toLocaleString()}</div>
          <div className="drill-down-stat-label">Violations</div>
          {summaryStats.violationChange && (
            <div className={`drill-down-stat-change ${parseFloat(summaryStats.violationChange) >= 0 ? 'negative' : 'positive'}`}>
              {parseFloat(summaryStats.violationChange) >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {summaryStats.violationChange}% vs prev
            </div>
          )}
        </div>
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value">{summaryStats.totalInspections.toLocaleString()}</div>
          <div className="drill-down-stat-label">Inspections</div>
        </div>
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value" style={{ color: 'var(--color-danger)' }}>
            {summaryStats.totalCrashes.toLocaleString()}
          </div>
          <div className="drill-down-stat-label">Crashes</div>
        </div>
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value">{summaryStats.oosRate}%</div>
          <div className="drill-down-stat-label">OOS Rate</div>
        </div>
        <div className="drill-down-stat-item">
          <div className="drill-down-stat-value">{summaryStats.inspectionRate}%</div>
          <div className="drill-down-stat-label">Violation Rate</div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="drill-down-detail-grid">
        {/* Day-by-Day Breakdown */}
        <div className="drill-down-detail-card" style={{ gridColumn: 'span 2' }}>
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <Activity size={18} style={{ color: 'var(--color-primary)' }} />
              Day-by-Day Activity
            </h4>
          </div>
          {dayByDayData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={dayByDayData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: '0.8125rem' }} />
                <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                <Area
                  type="monotone"
                  dataKey="inspections"
                  stackId="1"
                  stroke="#3b82f6"
                  fill="#3b82f6"
                  fillOpacity={0.3}
                  name="Inspections"
                />
                <Area
                  type="monotone"
                  dataKey="violations"
                  stackId="2"
                  stroke="#f59e0b"
                  fill="#f59e0b"
                  fillOpacity={0.5}
                  name="Violations"
                />
                <Area
                  type="monotone"
                  dataKey="crashes"
                  stackId="3"
                  stroke="#ef4444"
                  fill="#ef4444"
                  fillOpacity={0.7}
                  name="Crashes"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No daily data available
            </div>
          )}
        </div>

        {/* Day of Week Distribution */}
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <Clock size={18} style={{ color: 'var(--color-info)' }} />
              Day of Week Pattern
            </h4>
          </div>
          {dayOfWeekDistribution.some(d => d.count > 0) ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={dayOfWeekDistribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: '0.8125rem' }} />
                <Bar dataKey="count" fill="#8b5cf6" name="Violations" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No day of week data available
            </div>
          )}
        </div>

        {/* BASIC Category Distribution */}
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <AlertTriangle size={18} style={{ color: 'var(--color-warning)' }} />
              BASIC Categories
            </h4>
          </div>
          {basicDistribution.length > 0 ? (
            <div className="chart-clickable">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={basicDistribution}
                    dataKey="count"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label={({ percent }) => `${((percent as number) * 100).toFixed(0)}%`}
                    onClick={(data: CategoryChartData) => handleCategoryClick(data.category)}
                    cursor="pointer"
                  >
                    {basicDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ fontSize: '0.8125rem' }}
                    formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No category data available
            </div>
          )}
        </div>

        {/* Geographic Distribution */}
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <MapPin size={18} style={{ color: 'var(--color-success)' }} />
              Top States
            </h4>
          </div>
          {geographicDistribution.length > 0 ? (
            <div className="chart-clickable">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={geographicDistribution} layout="vertical" margin={{ left: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="state" tick={{ fontSize: 11 }} width={35} />
                  <Tooltip contentStyle={{ fontSize: '0.8125rem' }} />
                  <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                  <Bar
                    dataKey="violations"
                    fill="#f59e0b"
                    name="Violations"
                    onClick={(data: unknown) => handleStateClick((data as StateChartData).state)}
                    cursor="pointer"
                  />
                  <Bar
                    dataKey="inspections"
                    fill="#3b82f6"
                    name="Inspections"
                    onClick={(data: unknown) => handleStateClick((data as StateChartData).state)}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              No geographic data available
            </div>
          )}
        </div>
      </div>

      {/* Crash Details (if any) */}
      {crashes.length > 0 && (
        <div className="drill-down-detail-card">
          <div className="drill-down-detail-card-header">
            <h4 className="drill-down-detail-card-title">
              <AlertTriangle size={18} style={{ color: 'var(--color-danger)' }} />
              Crash Details
            </h4>
            <span className="drill-down-detail-card-badge">
              {crashes.length} crashes
            </span>
          </div>
          <div className="drill-down-stats-row" style={{ marginBottom: '1rem' }}>
            <div className="drill-down-stat-item">
              <div className="drill-down-stat-value" style={{ color: 'var(--color-danger)' }}>
                {summaryStats.fatalities}
              </div>
              <div className="drill-down-stat-label">Fatalities</div>
            </div>
            <div className="drill-down-stat-item">
              <div className="drill-down-stat-value" style={{ color: 'var(--color-warning)' }}>
                {summaryStats.injuries}
              </div>
              <div className="drill-down-stat-label">Injuries</div>
            </div>
          </div>
          <div style={{ maxHeight: '300px', overflow: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border)', position: 'sticky', top: 0, backgroundColor: 'white' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Date</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>State</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Fatalities</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Injuries</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Weather</th>
                </tr>
              </thead>
              <tbody>
                {crashes.slice(0, 20).map((c, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.75rem' }}>
                      {c.reportDate ? new Date(c.reportDate).toLocaleDateString() : '-'}
                    </td>
                    <td style={{ padding: '0.75rem' }}>{c.reportState || '-'}</td>
                    <td style={{ padding: '0.75rem', textAlign: 'center', color: c.fatalities > 0 ? 'var(--color-danger)' : 'inherit' }}>
                      {c.fatalities || 0}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center', color: c.injuries > 0 ? 'var(--color-warning)' : 'inherit' }}>
                      {c.injuries || 0}
                    </td>
                    <td style={{ padding: '0.75rem' }}>{c.weatherCondition || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthDetailView;
